import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { usePaymentsApi } from "./usePaymentsApi";

export const paymentsQueryKeys = {
    propertyPayments: (propertyId) => ['payments', { propertyId }],
    /** Payments for one order (reservation) within a property */
    reservationPayments: (propertyId, reservationId) => ['payments', { propertyId, reservationId }],
};

/**
 * Loads payments for a property, optionally filtered by order id.
 * @param {string|null|undefined} propertyId - Required for API calls and query enablement
 * @param {string|number|null|undefined} [reservationId] - When set, list is scoped to that order
 */
export const usePaymentsData = (propertyId, reservationId) => {
    const { findAllPayments, createPayment, updatePayment, deletePayment } = usePaymentsApi();
    const queryClient = useQueryClient();

    const queryKey = reservationId ? paymentsQueryKeys.reservationPayments(propertyId, reservationId) : paymentsQueryKeys.propertyPayments(propertyId);

    const cachedPayments = queryClient.getQueryData(queryKey);

    const queryResults = useQuery({
        queryKey,
        queryFn: () => findAllPayments(propertyId, reservationId),
        staleTime: defaultStaleTime,
        enabled: !!propertyId,
    });

    const _createPayment = async (payment) => {
        const createdPayment = await createPayment(propertyId, payment);
        const updatedPayments = cachedPayments ? [...cachedPayments, createdPayment] : [createdPayment];
        queryClient.setQueryData(queryKey, updatedPayments);
    };

    const _updatePayment = async (payment) => {
        const updatedPayment = await updatePayment(propertyId, payment);
        const updatedPayments = cachedPayments?.map(cachedPayment =>
            String(cachedPayment.id) === String(updatedPayment.id) ? updatedPayment : cachedPayment
        );
        queryClient.setQueryData(queryKey, updatedPayments);
    };

    const _deletePayment = async (paymentId) => {
        await deletePayment(propertyId, paymentId);
        const updatedPayments = cachedPayments?.filter(cachedPayment => String(cachedPayment.id) !== String(paymentId));
        queryClient.setQueryData(queryKey, updatedPayments);
    };

    return {
        queryResults,
        createPayment: _createPayment,
        updatePayment: _updatePayment,
        deletePayment: _deletePayment,
    };
};
